import type { Aabb, InputMove, MovementConfig } from "@townsy/contracts";
import { buildColliderIndex, stepMotion, type ColliderIndex, type MotionState, type Obb } from "@townsy/world";

/**
 * Client-side prediction and reconciliation for the local avatar (docs/03 section 3, brief 5).
 *
 * The client runs the SAME `stepMotion` the server runs, at the server's fixed timestep
 * (1 / simulationHz). Every sent input is kept with its sequence number; when an authoritative
 * state arrives with `lastSeq`, we rewind to the server position and replay the inputs the
 * server has not acknowledged yet. With identical inputs and geometry the replay reproduces the
 * prediction exactly, so corrections only appear when the server disagreed (e.g. a wall, a
 * rejected input, or a teleport such as "Return to plaza").
 *
 * Cars (ADR-0006) are oriented obstacles: the session supplies their current boxes
 * (`setObstacleSource`), exactly as the server passes every car to `stepMotion`.
 *
 * World v3 (ADR-0008): the static colliders are queried through a spatial index
 * (`buildColliderIndex`), rebuilt whenever the collider list changes. The index returns exactly the
 * boxes the plain list would hit, so every step and every replay gives the same result as before,
 * at a cost that no longer grows with the size of the city.
 */
export interface AuthoritativeState {
  x: number;
  z: number;
  facing: number;
  anim: 0 | 1 | 2;
  lastSeq: number;
  /** The car the actor drives ("" while walking; ActorState.vehicleId). */
  vehicleId?: string;
  /** 0 standing, 1 sitting, 2 lying (ActorState.posture, ADR-0018): no walking is predicted while seated. */
  posture?: 0 | 1 | 2;
  /** The seat while sitting / lying ("" while standing). */
  seatId?: string;
}

/** One walking step's input: the move, the facing and the button bits (`INPUT_BUTTONS.run`, ADR-0018). */
export type MoveIntent = Pick<InputMove, "moveX" | "moveZ" | "facing"> & { buttons?: number };

export interface PendingInput {
  seq: number;
  input: MoveIntent;
}

/** Unacknowledged inputs are capped to a few seconds of history; older ones can no longer be replayed. */
export const MAX_PENDING_INPUTS = 120;

export class Predictor {
  state: MotionState;
  readonly dt: number;
  private pending: PendingInput[] = [];
  private nextSeq = 1;
  private lastAck = 0;
  private colliders: readonly Aabb[];
  /** Spatial index over `colliders` (rebuilt with them; never out of date). */
  private index: ColliderIndex;
  private bounds: Aabb;
  private obstacleSource: () => readonly Obb[] = () => [];
  /** Distance between the predicted and the replayed state at the last reconciliation (diagnostics). */
  lastCorrection = 0;

  constructor(
    private readonly cfg: MovementConfig,
    colliders: readonly Aabb[],
    bounds: Aabb,
    initial: MotionState = { x: 0, z: 0, facing: 0, anim: 0 },
  ) {
    this.dt = 1 / cfg.simulationHz;
    this.colliders = colliders;
    this.index = buildColliderIndex(colliders);
    this.bounds = bounds;
    this.state = { ...initial };
  }

  /** Swap the collision environment (street <-> interior). Pending history is discarded. */
  setEnvironment(colliders: readonly Aabb[], bounds: Aabb): void {
    this.colliders = colliders;
    this.index = buildColliderIndex(colliders);
    this.bounds = bounds;
    this.pending = [];
  }

  /**
   * Replace the colliders inside the SAME room (e.g. a residential plot became owned and now has a
   * home collider). Pending inputs are kept: the next reconciliation replays them against the new
   * geometry, exactly as the server does after it refreshes its own owned set.
   */
  setColliders(colliders: readonly Aabb[]): void {
    if (colliders === this.colliders) return;
    this.colliders = colliders;
    this.index = buildColliderIndex(colliders);
  }

  get colliderCount(): number {
    return this.colliders.length;
  }

  /** The static colliders in use (the drive environment shares them with walking, as on the server). */
  get staticColliders(): readonly Aabb[] {
    return this.colliders;
  }

  /** The same colliders as a spatial index, for `stepVehicle` (DriveEnv.colliders takes a list or an index). */
  get colliderIndex(): ColliderIndex {
    return this.index;
  }

  /** Where the current car boxes come from (street rooms); read once per step / replay. */
  setObstacleSource(source: () => readonly Obb[]): void {
    this.obstacleSource = source;
  }

  /** The next sequence number this predictor will hand out. */
  get nextSequence(): number {
    return this.nextSeq;
  }

  /**
   * Never hand out a sequence number below `next` (driving used numbers in between: one strictly
   * increasing sequence per actor and room, whichever message carries it).
   */
  ensureNextSeq(next: number): void {
    if (Number.isFinite(next) && next > this.nextSeq) this.nextSeq = Math.floor(next);
  }

  /** Hard reset to an authoritative state (spawn, room change, reconnect). */
  reset(state: AuthoritativeState | MotionState): void {
    this.state = { x: state.x, z: state.z, facing: state.facing, anim: state.anim };
    this.pending = [];
    if ("lastSeq" in state) {
      this.lastAck = state.lastSeq;
      if (this.nextSeq <= state.lastSeq) this.nextSeq = state.lastSeq + 1;
    }
  }

  get pendingCount(): number {
    return this.pending.length;
  }

  get acknowledgedSeq(): number {
    return this.lastAck;
  }

  /**
   * Advance the local actor by one simulation tick. When `record` is true the input is sequenced,
   * remembered for replay and returned as the message to send; idle ticks that are not sent must
   * not consume sequence numbers (the server only acknowledges what it received).
   */
  applyInput(input: MoveIntent, opts: { record: boolean } = { record: true }): InputMove | null {
    this.state = stepMotion(this.state, input, this.dt, this.cfg, this.index, this.bounds, this.obstacleSource());
    if (!opts.record) return null;
    const seq = this.nextSeq++;
    // The replay must use the same buttons the server applies (running steps are longer).
    const buttons = Number.isInteger(input.buttons) && input.buttons! >= 0 && input.buttons! <= 255 ? input.buttons! : 0;
    this.pending.push({ seq, input: { moveX: input.moveX, moveZ: input.moveZ, facing: input.facing, buttons } });
    if (this.pending.length > MAX_PENDING_INPUTS) this.pending.splice(0, this.pending.length - MAX_PENDING_INPUTS);
    return { seq, moveX: input.moveX, moveZ: input.moveZ, facing: input.facing, buttons };
  }

  /** Rewind to the authoritative state and replay every input with `seq > lastSeq`. */
  reconcile(server: AuthoritativeState): void {
    if (server.lastSeq < this.lastAck) return; // out-of-order / stale acknowledgement
    this.lastAck = server.lastSeq;
    this.pending = this.pending.filter((p) => p.seq > server.lastSeq);
    let s: MotionState = { x: server.x, z: server.z, facing: server.facing, anim: server.anim };
    if (this.pending.length > 0) {
      const obstacles = this.obstacleSource();
      for (const p of this.pending) s = stepMotion(s, p.input, this.dt, this.cfg, this.index, this.bounds, obstacles);
    }
    this.lastCorrection = Math.hypot(s.x - this.state.x, s.z - this.state.z);
    this.state = s;
  }
}
